import { FC } from "react";
import "./PersonalAccountBeneficiaries.scss";
import {
  CoreFieldValue,
  LoadingV2,
  PrimaryButton,
  PrimaryCard,
  SectionPlaceholder,
} from "@ucl/library";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { PersonalAccountBeneficiaryForm } from "./PersonalAccountBeneficiaryForm";
import { PersonalAccountBeneficiaryTableRow } from "./PersonalAccountBeneficiaryTableRow";
import { usePersonalAccountBeneficiaries } from "@/hooks/usePersonalAccountBeneficiaries";
import {
  FinancialApplicationFormValidationDisplayBehavior,
  useFinancialApplicationFormSectionValidation,
  FinancialApplicationFormSectionContextType,
} from "@/hooks/useFinancialApplicationFormSectionValidation";
import { FinancialApplicationSection } from "@/types/enums";
import { MessageComponent } from "@syncfusion/ej2-react-notifications";

export interface PersonalAccountBeneficiariesProps {
  financialApplicationId: string;
  initiallyCollapsePersonalAccountBeneficiaryForms?: boolean;
  readOnly?: boolean;
  renderCollapsedAsTable?: boolean;
  onBlur?: (field: CoreFieldValue) => void;
  allowChangingWhetherCollapsed?: boolean;
  validationBehavior?: FinancialApplicationFormValidationDisplayBehavior;
  validationContextType: FinancialApplicationFormSectionContextType;
}

export const PersonalAccountBeneficiaries: FC<PersonalAccountBeneficiariesProps> = (props) => {
  const {
    personalAccountBeneficiaryIds,
    handleDeletePersonalAccountBeneficiary,
    handleAddPersonalAccountBeneficiary,
    isLoading,
    isCreating,
  } = usePersonalAccountBeneficiaries(props.financialApplicationId);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { getCollectionValidationErrors } = useFinancialApplicationFormSectionValidation(
    props.financialApplicationId,
    FinancialApplicationSection.PersonalAccountBeneficiaries,
    props.validationBehavior,
    props.validationContextType
  );

  const validationErrors = getCollectionValidationErrors("PersonalAccountBeneficiaries");
  const depositApplicationValidationErrors = getCollectionValidationErrors("DepositApplications");

  const totalPercentageBequeathedErrors = Object.values(
    depositApplicationValidationErrors?.collectionMemberValidationErrors ?? {}
  ).flatMap(allErrors => allErrors.map(error => error.errorMessage)) ?? [];

  return (
    <div className="personal-account-beneficiary-info-accounts">
      {!props.readOnly && (
        <div className="personal-account-beneficiary-info-accounts__header-info-validations">
          {totalPercentageBequeathedErrors.map((error, index) => (
            <MessageComponent title={error} severity="Error" key={`bequests-error-${index}`}>
              {error}
            </MessageComponent>
          ))}
        </div>
      )}
      <div className="personal-account-beneficiary-info-accounts__header">
        {!props.readOnly && (
          <PrimaryButton
            text="Add Beneficiary"
            iconProps={{ icon: faPlus }}
            onClick={handleAddPersonalAccountBeneficiary}
            disabled={props.readOnly}
            isLoading={isCreating}
            loadingProgressContent="Add Beneficiary"
            loadingSuccessContent="Add Beneficiary"
          />
        )}
      </div>
      <div className="personal-account-beneficiary-info-accounts__content">
        <PrimaryCard>
          {isLoading && <LoadingV2 />}
          {!isLoading && !props.readOnly && personalAccountBeneficiaryIds.length === 0 && (
            <SectionPlaceholder label="No beneficiaries. Add one to get started." />
          )}
          {!isLoading && props.readOnly && personalAccountBeneficiaryIds.length === 0 && (
            <div className="empty-state-message">
              <p>No beneficiaries have been added.</p>
            </div>
          )}
          {!isLoading &&
            props.renderCollapsedAsTable &&
            personalAccountBeneficiaryIds.length > 0 && (
              <div className="personal-account-beneficiaries-table">
                <table>
                  <thead>
                    <tr>
                      <th>Relationship</th>
                      <th>Personal Information</th>
                      <th>Beneficiary Designations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {personalAccountBeneficiaryIds.map((personalAccountBeneficiaryId) => (
                      <PersonalAccountBeneficiaryTableRow
                        key={personalAccountBeneficiaryId}
                        financialApplicationId={props.financialApplicationId}
                        personalAccountBeneficiaryId={personalAccountBeneficiaryId}
                        validationErrors={
                          validationErrors.fieldSchemaCollectionMemberValidationErrors[
                            personalAccountBeneficiaryId
                          ] || {}
                        }
                        onDelete={handleDeletePersonalAccountBeneficiary}
                        readOnly={props.readOnly}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          {!isLoading &&
            !props.renderCollapsedAsTable &&
            personalAccountBeneficiaryIds.length > 0 && (
              <div className="personal-account-beneficiaries-forms">
                {personalAccountBeneficiaryIds.map((personalAccountBeneficiaryId) => (
                  <PersonalAccountBeneficiaryForm
                    key={personalAccountBeneficiaryId}
                    financialApplicationId={props.financialApplicationId}
                    personalAccountBeneficiaryId={personalAccountBeneficiaryId}
                    validationErrors={validationErrors.fieldSchemaCollectionMemberValidationErrors}
                    readOnly={props.readOnly}
                    onDelete={handleDeletePersonalAccountBeneficiary}
                    isInitiallyCollapsed={props.initiallyCollapsePersonalAccountBeneficiaryForms}
                    allowChangingWhetherCollapsed={props.allowChangingWhetherCollapsed}
                    validationContextType={props.validationContextType}
                  />
                ))}
              </div>
            )}
        </PrimaryCard>
      </div>
    </div>
  );
};
